/**
 * Decision Records
 *
 * The read surface for the Ambient Runtime judgment-lane artifacts:
 * decision narratives (AMB-4), evidence briefs (AMB-5), and outcome
 * retrospectives (AMB-7). Before this page these tables were written by the
 * `ambient` edge function but had no viewer anywhere in the app.
 */

import { useEffect, useMemo, useState } from 'react';
import { ScrollText, FileSearch, ClipboardCheck, ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { PageHeader } from '../components/prioritization/PageHeader';
import { PorNarrativeMarkdown } from '../components/plan-of-record/PorNarrativeMarkdown';
import { getCurrentWorkspaceId } from '../lib/supabaseSync';
import {
  fetchDecisionRecords,
  type DecisionRecord,
  type DecisionRecordKind,
} from '../lib/decisionRecordsService';

const KIND_META: Record<
  DecisionRecordKind,
  { label: string; short: string; icon: typeof ScrollText; badge: string }
> = {
  decision_narrative: {
    label: 'Decision narratives',
    short: 'Narrative',
    icon: ScrollText,
    badge: 'bg-primary-50 text-primary-700 border-primary-200',
  },
  evidence_brief: {
    label: 'Evidence briefs',
    short: 'Evidence',
    icon: FileSearch,
    badge: 'bg-[var(--mustard-dim)] text-[var(--mustard)] border-[var(--mustard)]',
  },
  outcome_retrospective: {
    label: 'Outcome retrospectives',
    short: 'Retrospective',
    icon: ClipboardCheck,
    badge: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] border-[var(--green)]',
  },
};

type Filter = 'all' | DecisionRecordKind;

function fmtDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export function DecisionRecords() {
  const workspaceId = getCurrentWorkspaceId();
  const [records, setRecords] = useState<DecisionRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!workspaceId) {
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    fetchDecisionRecords(workspaceId).then(({ data, error }) => {
      if (!active) return;
      setRecords(data);
      setError(error);
      setLoading(false);
    });
    return () => {
      active = false;
    };
  }, [workspaceId]);

  const counts = useMemo(() => {
    const c: Record<Filter, number> = {
      all: records.length,
      decision_narrative: 0,
      evidence_brief: 0,
      outcome_retrospective: 0,
    };
    for (const r of records) c[r.kind]++;
    return c;
  }, [records]);

  const filtered = filter === 'all' ? records : records.filter((r) => r.kind === filter);

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const tabs: { id: Filter; label: string }[] = [
    { id: 'all', label: 'All' },
    { id: 'decision_narrative', label: KIND_META.decision_narrative.label },
    { id: 'evidence_brief', label: KIND_META.evidence_brief.label },
    { id: 'outcome_retrospective', label: KIND_META.outcome_retrospective.label },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<ScrollText className="h-4 w-4 text-primary-700" />}
        title="Decision Records"
        subtitle="Narratives, evidence briefs, and outcome retrospectives your ambient agents have produced."
      />

      {error && <div className="card bg-[var(--coral-dim)] border-[var(--coral)] text-sm text-[var(--coral)]">{error}</div>}

      <div className="flex flex-wrap gap-2">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setFilter(t.id)}
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
              filter === t.id
                ? 'border-primary-300 bg-primary-50 text-primary-700'
                : 'border-[var(--rule)] bg-[var(--bg-raised)] text-[var(--text-ter)] hover:bg-[var(--surface)]'
            }`}
          >
            {t.label}
            <span className="rounded-full bg-black/[0.06] px-1.5 py-0.5 text-[0.6875rem] tabular-nums text-[var(--text-ter)]">
              {counts[t.id]}
            </span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="card flex items-center gap-2 text-sm text-[var(--text-ter)]">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading decision records…
        </div>
      ) : filtered.length === 0 ? (
        <div className="card text-sm text-[var(--text-ter)]">
          No decision records yet. They appear here after the Decision Narrative, Evidence Brief, and
          Outcome Ledger agents run against your ratified priority lists.
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((r) => {
            const meta = KIND_META[r.kind];
            const Icon = meta.icon;
            const isOpen = expanded.has(r.id);
            return (
              <div key={r.id} className="card">
                <button
                  onClick={() => toggle(r.id)}
                  className="flex w-full items-start gap-3 text-left"
                >
                  <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center bg-[var(--hover-tint)]">
                    <Icon className="h-4 w-4 text-[var(--text-sec)]" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold text-[var(--text-pri)]">{r.title}</span>
                      <span className={`rounded-full border px-2 py-0.5 text-[0.6875rem] font-medium ${meta.badge}`}>
                        {meta.short}
                      </span>
                      {r.date && <span className="text-xs text-[var(--text-ter)]">{fmtDate(r.date)}</span>}
                    </span>
                    {r.summary && !isOpen && (
                      <span className="mt-1 block truncate text-xs text-[var(--text-ter)]">{r.summary}</span>
                    )}
                  </span>
                  {isOpen ? (
                    <ChevronDown className="mt-1 h-4 w-4 shrink-0 text-[var(--text-ter)]" />
                  ) : (
                    <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-[var(--text-ter)]" />
                  )}
                </button>

                {isOpen && (
                  <div className="mt-4 border-t border-[var(--rule)] pt-4">
                    <PorNarrativeMarkdown source={r.markdown} emptyHint="No detail recorded for this entry." />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default DecisionRecords;
